<template>
  <div v-if="!auth.ready" class="flex h-full items-center justify-center">
    <AltSpinner size="lg" />
  </div>

  <template v-else>
    <!-- Pages publiques (connexion) et pages « pleines » (erreurs) : sans menu. -->
    <RouterView v-if="isPublic || isPlein || !auth.isAuthenticated" />

    <AltAppLayout
      v-else
      :items="nav"
      brand="altoneo"
      title="Indemnités"
      :page-title="pageTitle"
      :active-path="cheminActif"
      :link-component="RouterLink"
    >
      <template #sidebar-icon="{ item }">
        <NavIcon :nom="iconeDe(item)" />
      </template>

      <template #topbar-actions>
        <!--
          LE GESTE LE PLUS FRÉQUENT, TOUJOURS À PORTÉE : en haut de chaque écran,
          y compris sur téléphone où le menu latéral est replié. Il n'est pas
          dans le menu : « /declarations/nouvelle » y allumerait aussi « Mes
          déclarations » (surlignage par préfixe d'AltSidebar).
        -->
        <AltButton v-if="route.name !== 'declaration-nouvelle'" data-tour="nouvelle-declaration" size="sm" @click="router.push('/declarations/nouvelle')">
          <span aria-hidden="true">+</span>
          <span class="hidden sm:inline">Nouvelle déclaration</span>
          <span class="sr-only sm:hidden">Nouvelle déclaration</span>
        </AltButton>
        <ProfileMenu data-tour="profil" />
      </template>

      <RouterView v-slot="{ Component, route: r }">
        <Transition name="fade" mode="out-in">
          <!-- La génération change à chaque bascule de monde (démo, rôle) : l'écran se remonte et recharge. -->
          <component :is="Component" :key="`${r.path}#${decouverte.generation}`" />
        </Transition>
      </RouterView>
    </AltAppLayout>
  </template>

  <!-- Monté hors du `v-else` : une confirmation peut se poser à tout moment. -->
  <ConfirmerHote />
  <!-- Mode démo et visite guidée : au-dessus de tout, y compris hors coquille. -->
  <BandeauDemo />
  <VisiteGuidee />
  <AltToastContainer position="bottom-right" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import { createToastStore } from 'altoneo-charting';
import type { DeclarationDto, Page } from '@igd/shared';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import ConfirmerHote from '@/components/ConfirmerHote.vue';
import ProfileMenu from '@/components/ProfileMenu.vue';
import NavIcon from '@/components/NavIcon.vue';
import BandeauDemo from '@/components/decouverte/BandeauDemo.vue';
import VisiteGuidee from '@/components/decouverte/VisiteGuidee.vue';
import { useDecouverteStore } from '@/stores/decouverte';

// Le magasin de toasts DOIT être fourni par la racine : `useToast()` l'injecte.
createToastStore();

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const decouverte = useDecouverteStore();

const isPublic = computed(() => route.meta.public === true);
const isPlein = computed(() => route.meta.plein === true);
const pageTitle = computed(() => route.meta.title ?? '');

/**
 * Le détail et le formulaire d'une déclaration allument « Mes déclarations » :
 * le surlignage par préfixe d'AltSidebar le fait déjà. Seul cas particulier : le détail ouvert DEPUIS la validation doit laisser
 * « À valider » allumé — on le sait par `?depuis=validation`.
 */
const cheminActif = computed(() => (route.query.depuis === 'validation' ? '/validation' : route.path));

/**
 * LA PASTILLE « À VALIDER » : le nombre de déclarations sur lesquelles j'ai une
 * décision possible. Rafraîchie à chaque navigation — après une validation, on
 * revient à la liste et le compteur doit déjà avoir baissé.
 */
const aTraiter = ref(0);
const estValideur = computed(() => auth.hasRole('manager', 'rh', 'admin'));
watch(
  () => [auth.isAuthenticated, route.path, decouverte.generation] as const,
  async ([connecte]) => {
    if (!connecte || !estValideur.value) {
      aTraiter.value = 0;
      return;
    }
    try {
      const p = await api.get<Page<DeclarationDto>>('/declarations?vue=a_traiter&page=1&pageSize=1');
      aTraiter.value = p.total;
    } catch {
      /* compteur non critique */
    }
  },
  { immediate: true },
);

type Icone = 'accueil' | 'declarations' | 'nouvelle' | 'validation' | 'paie' | 'admin' | 'boussole' | 'point';
const ICONES: Record<string, Icone> = {
  '/': 'accueil',
  '/declarations': 'declarations',
  '/validation': 'validation',
  '/paie': 'paie',
  '/admin': 'admin',
  '/decouverte': 'boussole',
};
const iconeDe = (item: { to?: string }): Icone => ICONES[item.to ?? ''] ?? 'point';

/** Le menu suit les rôles : on ne propose pas un écran que l'API refuserait. */
const nav = computed(() => [
  { heading: 'Mes déplacements' },
  { to: '/', label: 'Tableau de bord' },
  { to: '/declarations', label: 'Mes déclarations' },
  ...(estValideur.value
    ? [{ heading: 'Validation' }, { to: '/validation', label: 'À valider', badge: aTraiter.value || undefined }]
    : []),
  ...(auth.hasRole('rh') ? [{ heading: 'Paie' }, { to: '/paie', label: 'Export paie & reporting' }] : []),
  ...(auth.hasRole('admin') ? [{ heading: 'Administration' }, { to: '/admin', label: 'Administration' }] : []),
  { heading: 'Aide' },
  { to: '/decouverte', label: 'Découverte' },
]);
</script>
